// 서비스 전체 원가/마진 계산에 쓰는 유일한 환율 상수 — export 해서 RevenueCostChart/TrendTable
// 등 "USD 원가를 NC(=KRW)와 비교 가능한 값으로" 바꿔야 하는 곳들이 각자 1500을 새로 쓰지
// 않고 이걸 가져다 쓰게 한다(2026-07-17, 매출·원가·순이익 차트 추가하며 정리).
export const KRW_PER_USD = 1500;

// 지출액 = 실제 나간 돈 기준(2026-07-21 변경) — 관리자가 /analytics/expenses 로 입력한
// 실청구액(AWS 인프라·AI 구독료·인건비·마케팅비, row.expenseKrw) + OpenAI Costs API
// 실청구(row.openaiKrw, TrendSection 이 일별로 병합해 넣음) + BillingON AWS 실청구
// (row.awsKrw, 같은 방식으로 병합 — 2026-07-27). 예전엔 Bedrock 토큰×정가
// 추정치(usdCost×1500)를 지출로 썼는데 실청구서와 어긋나는 추정값이라 지출에서 제외 —
// usdCost 는 이제 참고 지표(지출 세부내역의 모델별 추정, FinOps 마진 배수)로만 쓰인다.

// AWS 인프라비 한 축의 확정값 — 자동(BillingON)이 기본이고, 그 날짜에 수기 입력
// (category="server", row.serverExpenseKrw)이 있으면 수기가 이긴다(2026-07-27).
// 이유: BillingON 은 1~2일 지연으로 당일/전일이 $0 또는 부분값이고, 월 청구서 확정 후
// 크레딧·할인·세금 차이를 사람이 정정해야 하는 경우가 있다. 자동값을 "숨기는" 게 아니라
// 덮는 것이므로 원본 두 값(awsKrw / serverExpenseKrw)은 행에 그대로 남겨 화면에서
// 출처와 차이를 보여줄 수 있게 한다.
// awsAppliedKrw 가 있으면 그걸 쓴다 — 주/월로 재집계할 때 이 우선순위는 "일자별"로
// 판정한 뒤 합산해야 맞기 때문에(합산 후 판정하면 수기가 있는 하루가 그 주 전체의
// 자동값을 덮어버린다) rebucketSeries 가 일자별 결과를 미리 합산해 넣어둔다.
export function awsExpenseKrw(row) {
  if (row?.awsAppliedKrw !== undefined) return row.awsAppliedKrw;
  const manual = row?.serverExpenseKrw || 0;
  return manual > 0 ? manual : (row?.awsKrw || 0);
}

// AWS 청구액이 아직 자동 조회도 수기 입력도 안 된 기간은 지출이 과소 표시된다(화면에 안내 문구).
export function totalExpenseKrw(row) {
  // expenseKrw 는 수기 입력 총액(server 포함)이라, AWS 축을 따로 더하기 전에 그 안의
  // server 분을 빼야 이중 계상되지 않는다.
  const manualNonAws = (row?.expenseKrw || 0) - (row?.serverExpenseKrw || 0);
  return manualNonAws + awsExpenseKrw(row) + (row?.openaiKrw || 0);
}

// 순이익 = 매출(row.revenueKrw, 그 구간에 실제로 결제된 토스 금액) - 지출액. "매출"은
// NC 소진량(ncCost)이 아니라 실제 결제금액 기준(2026-07-17) — ncCost는 "서비스를 얼마나
// 썼는지"고 revenueKrw는 "돈을 얼마나 받았는지"라 시점도 다르고(충전 시점 vs 사용 시점)
// 프로모션 배율이 1:1이 아니면 금액도 갈린다. rebucketSeries/cumulativeSeries 출력 어느
// 쪽에 넣어도 동작한다(둘 다 revenueKrw/usdCost/expenseKrw 를 이미 올바르게 합산/누적해
// 두므로).
// ⚠️ 이름은 netProfit 이지만 회계상 순이익이 아니다(2026-08-13 B-1). 실제 의미는
// **직접비 기여이익(Direct Contribution)** — 매출에서 "추적된 직접비"만 뺀 값이다.
// 빠진 것: PG 수수료 · VAT · 인건비 · 마케팅비 · 미태깅 AWS · Exa(실청구 소스 없음).
// 식별자는 호출부 6곳과의 호환 때문에 유지한다 — 화면 라벨만 정정했다.
export function netProfitKrw(row) {
  return (row?.revenueKrw || 0) - totalExpenseKrw(row);
}


// 주 경계 = 목요일 시작 ~ 수요일 끝(2026-07-16, 팀 주간 집계 관행에 맞춤 — ISO 주(월~일) 아님).
export function weekStart(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const day = d.getUTCDay(); // Sun=0..Sat=6
  const daysSinceThursday = (day - 4 + 7) % 7; // Thu=4 기준 며칠 지났는지
  d.setUTCDate(d.getUTCDate() - daysSinceThursday);
  return d.toISOString().slice(0, 10);
}

export function monthStart(dateStr) {
  return dateStr.slice(0, 7); // YYYY-MM
}

function addDaysISO(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function nextMonthISO(yyyyMm) {
  const [y, m] = yyyyMm.split("-").map(Number);
  const d = new Date(Date.UTC(y, m, 1)); // m(1-indexed 그 달) 을 0-indexed 로 넣으면 곧 "다음 달"
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

// [from, to] 구간에 실제로 존재해야 할 버킷 키를 빠짐없이 나열한다. 백엔드 시계열은 활동이
// 전혀 없는 날/주/월은 버킷 자체를 안 만들어서 결과 배열에서 통째로 빠지는데, recharts 는
// 카테고리 축이라 그 빠진 구간과 이웃 구간의 실제 시간 간격(예: 10일 vs 2일)을 구분 못하고
// 똑같은 폭으로 그린다 — 즉 x축이 실제 경과 시간과 비례하지 않는 왜곡이 생긴다(2026-07-17,
// UI 리뷰 중 발견). rebucket 함수들이 이 목록으로 빈 구간을 0으로 미리 채워 넣어 방지한다.
export function generateBucketKeys(from, to, granularity) {
  const keys = [];
  if (granularity === "month") {
    let cur = monthStart(from);
    const end = monthStart(to);
    while (cur <= end) {
      keys.push(cur);
      cur = nextMonthISO(cur);
    }
    return keys;
  }
  const step = granularity === "week" ? 7 : 1;
  let cur = granularity === "week" ? weekStart(from) : from;
  const end = granularity === "week" ? weekStart(to) : to;
  while (cur <= end) {
    keys.push(cur);
    cur = addDaysISO(cur, step);
  }
  return keys;
}

const DOW_KR = ["일", "월", "화", "수", "목", "금", "토"];

// 화면에 보여줄 기간 라벨. bucket 값(day/week 는 "YYYY-MM-DD", month 는 "YYYY-MM")만으로는
// "이게 무슨 기간인지" 한눈에 안 들어와서(특히 week 는 시작일 하나만 보여서 끝나는 날을 몰랐음),
// day 는 요일까지, week 는 목~수 전체 범위를, month 는 "N년 M월"로 명시한다(2026-07-16).
export function formatBucketLabel(bucket, granularity) {
  if (!bucket) return bucket;
  if (granularity === "month") {
    const [y, m] = bucket.split("-");
    return `${y}년 ${Number(m)}월`;
  }
  const start = new Date(bucket + "T00:00:00Z");
  const md = (d) => `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
  if (granularity === "week") {
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return `${md(start)}~${md(end)}`;
  }
  return `${md(start)}(${DOW_KR[start.getUTCDay()]})`;
}

// 메시지 수 = 요청(=AI 응답 완료) 1건당 사용자 메시지 1개 + AI 응답 1개, 정확히 2개.
// save_message() 가 매 완료된 턴마다 정확히 이 순서로 호출돼서 근사치가 아니라 정확한 값이다.
function withDerivedCounts(row) {
  const messageCount = (row.requestCount || 0) * 2;
  return {
    ...row,
    messageCount,
    messagesPerSession: row.sessionCount > 0 ? messageCount / row.sessionCount : 0,
  };
}

function emptyAgg() {
  return {
    ncCost: 0, ncCharged: 0, requestCount: 0, tokens: 0, usdCost: 0, cacheHitWeighted: 0,
    activeUserSet: new Set(), sessionSet: new Set(), revenueKrw: 0, expenseKrw: 0, openaiKrw: 0,
    serverExpenseKrw: 0, awsKrw: 0, awsAppliedKrw: 0,
  };
}

// Re-buckets a daily analytics series into day/week/month buckets on the client.
// range({from,to}, 둘 다 "YYYY-MM-DD")를 주면 그 구간의 모든 버킷을 0으로 미리 채워서
// 활동 없는 구간이 배열에서 빠지지 않게 한다(위 generateBucketKeys 설명 참고) — 안 주면
// (예전처럼) 실제 데이터가 있는 버킷만 나온다.
export function rebucketSeries(series, granularity, range) {
  const keyFn = granularity === "week" ? weekStart : granularity === "month" ? monthStart : (d) => d;
  const by = new Map();
  if (range?.from && range?.to) {
    for (const bucket of generateBucketKeys(range.from, range.to, granularity)) {
      by.set(bucket, { bucket, ...emptyAgg() });
    }
  }
  for (const p of series || []) {
    const key = keyFn(p.bucket);
    if (!by.has(key)) by.set(key, { bucket: key, ...emptyAgg() });
    const row = by.get(key);
    row.ncCost += p.ncCost || 0;
    row.ncCharged += p.ncCharged || 0;
    row.requestCount += p.requestCount || 0;
    row.tokens += p.tokens || 0;
    row.usdCost += p.usdCost || 0;
    row.revenueKrw += p.revenueKrw || 0;
    row.expenseKrw += p.expenseKrw || 0;
    row.openaiKrw += p.openaiKrw || 0;
    row.serverExpenseKrw += p.serverExpenseKrw || 0;
    row.awsKrw += p.awsKrw || 0;
    // 자동/수기 우선순위는 반드시 일자별로 판정한 뒤 합산한다(위 awsExpenseKrw 주석 참고).
    row.awsAppliedKrw += awsExpenseKrw(p);
    row.cacheHitWeighted += (p.cacheHitRate || 0) * (p.requestCount || 0);
    // 활성 사용자/세션은 일별 개수를 그냥 더하면 같은 사람·같은 대화가 그 주에 여러 날
    // 걸쳐 있었던 만큼 중복 집계된다 — 실제 id 집합을 합집합(union)해야 "그 기간 동안의
    // 순수 인원/세션 수"가 된다.
    (p.activeUserIds || []).forEach((uid) => row.activeUserSet.add(uid));
    (p.sessionIds || []).forEach((sid) => row.sessionSet.add(sid));
  }
  if (by.size === 0) return [];
  return [...by.values()]
    .sort((a, b) => b.bucket.localeCompare(a.bucket))
    .map((row) => withDerivedCounts({
      bucket: row.bucket,
      ncCost: row.ncCost,
      ncCharged: row.ncCharged,
      revenueKrw: row.revenueKrw,
      expenseKrw: row.expenseKrw,
      openaiKrw: row.openaiKrw,
      serverExpenseKrw: row.serverExpenseKrw,
      awsKrw: row.awsKrw,
      awsAppliedKrw: row.awsAppliedKrw,
      requestCount: row.requestCount,
      tokens: row.tokens,
      usdCost: row.usdCost,
      // 마진% = 순이익률(순이익/매출) — 2026-07-21, 예전 "차감 NC 대비 AI 원가" 효율
      // 지표에서 사업 손익 지표로 변경. 매출 0이면 정의 불가라 0.
      // 지출 정의는 totalExpenseKrw 한 곳에서만 관리한다(AWS 축 추가 때 여기가 따로
      // 갱신 안 돼 마진%만 옛 정의로 남는 사고를 막기 위해 — 2026-07-27).
      marginPct: row.revenueKrw > 0
        ? (row.revenueKrw - totalExpenseKrw(row)) / row.revenueKrw : 0,
      cacheHitRate: row.requestCount > 0 ? row.cacheHitWeighted / row.requestCount : 0,
      activeUsers: row.activeUserSet.size,
      // 원본 id 배열도 들고 다닌다 — cumulativeSeries() 가 여러 구간에 걸친 합집합을
      // 다시 계산하려면(카운트만으로는 안 됨) 필요하다.
      activeUserIds: [...row.activeUserSet],
      sessionCount: row.sessionSet.size,
      sessionIds: [...row.sessionSet],
    }));
}

// rebucketSeries 결과(최신순)를 "구간별"이 아니라 "누적"(그 시점까지 총 얼마인지)으로 바꾼다.
// NC/토큰/메시지 수는 그냥 누적합이면 되지만, 활성 사용자·세션 수는 같은 사람/대화가 여러
// 구간에 걸쳐 있으면 단순 합산이 중복 집계가 되므로 id 집합을 이어붙인 합집합으로 계산한다.
// 마진%는 누적 NC/USD 로 다시 계산(비율은 단순 합산·평균이 성립하지 않음).
// 캐시히트%는 원본 성분(캐시 리드/전체 토큰 수)이 여기 없어 완전한 누적 재계산은 못하고
// 그 구간의 값을 그대로 이어간다 — 참고용으로만 볼 것.
export function cumulativeSeries(series) {
  if (!series || series.length === 0) return [];
  const asc = [...series].sort((a, b) => a.bucket.localeCompare(b.bucket));
  let ncCost = 0, ncCharged = 0, requestCount = 0, tokens = 0, usdCost = 0, revenueKrw = 0, expenseKrw = 0, openaiKrw = 0;
  let serverExpenseKrw = 0, awsKrw = 0, awsAppliedKrw = 0;
  const userSet = new Set();
  const sessionSet = new Set();
  const out = asc.map((r) => {
    ncCost += r.ncCost || 0;
    ncCharged += r.ncCharged || 0;
    requestCount += r.requestCount || 0;
    tokens += r.tokens || 0;
    usdCost += r.usdCost || 0;
    revenueKrw += r.revenueKrw || 0;
    expenseKrw += r.expenseKrw || 0;
    openaiKrw += r.openaiKrw || 0;
    serverExpenseKrw += r.serverExpenseKrw || 0;
    awsKrw += r.awsKrw || 0;
    // 이미 버킷별로 우선순위가 판정된 값(awsAppliedKrw)을 그대로 이어 더한다.
    awsAppliedKrw += awsExpenseKrw(r);
    (r.activeUserIds || []).forEach((u) => userSet.add(u));
    (r.sessionIds || []).forEach((s) => sessionSet.add(s));
    const sessionCount = sessionSet.size;
    const cumRow = {
      ...r,
      ncCost, ncCharged, requestCount, tokens, usdCost, revenueKrw, expenseKrw, openaiKrw,
      serverExpenseKrw, awsKrw, awsAppliedKrw,
      activeUsers: userSet.size,
      activeUserIds: [...userSet],
      sessionCount,
      sessionIds: [...sessionSet],
    };
    return withDerivedCounts({
      ...cumRow,
      marginPct: revenueKrw > 0 ? (revenueKrw - totalExpenseKrw(cumRow)) / revenueKrw : 0,
    });
  });
  return out.sort((a, b) => b.bucket.localeCompare(a.bucket));
}

// 백엔드 `/analytics/overview?includeSignup=1` 의 signupTrend([{date, newUsers,
// cumulativeUsers}], 일별·이미 누적 포함)를 주/월 단위로 재집계. _signup_stats() 는 요청
// 범위의 모든 날짜를 빠짐없이 채워서 반환하므로(newUsers=0인 날도 항목이 있음) 여기는
// generateBucketKeys 로 다시 채울 필요가 없다. newUsers 는 그냥 합산해도 되지만(가입
// 이벤트는 겹칠 수 없음), cumulativeUsers 는 이미 누적치라 합산하면 안 되고 그 구간의
// 마지막 날짜 값을 그대로 쓴다.
export function rebucketSignupSeries(trend, granularity) {
  if (!trend || trend.length === 0) return [];
  if (granularity === "day") {
    return [...trend]
      .map((r) => ({ bucket: r.date, newUsers: r.newUsers, cumulativeUsers: r.cumulativeUsers }))
      .sort((a, b) => b.bucket.localeCompare(a.bucket));
  }
  const keyFn = granularity === "week" ? weekStart : monthStart;
  const by = new Map();
  for (const r of trend) {
    const key = keyFn(r.date);
    if (!by.has(key)) by.set(key, { bucket: key, newUsers: 0, cumulativeUsers: 0, _lastDate: "" });
    const row = by.get(key);
    row.newUsers += r.newUsers || 0;
    if (r.date >= row._lastDate) {
      row.cumulativeUsers = r.cumulativeUsers;
      row._lastDate = r.date;
    }
  }
  return [...by.values()]
    .map(({ _lastDate, ...rest }) => rest)
    .sort((a, b) => b.bucket.localeCompare(a.bucket));
}

// 백엔드 `/analytics/timeseries?byTool=1` 의 toolSeries
// ([{bucket, byTool: {tool: count}, byToolEngine: {"tool|engine": count},
//   byToolEngineSessionIds: {"tool|engine": [conversationId,...]}}])를 일별→주/월별로 재집계.
// range 를 주면 rebucketSeries 와 동일하게 활동 없는 버킷도 0으로 채워 x축 왜곡을 막는다.
// 요청 수는 그냥 더해도 되는 값(유저 수와 달리 중복 집계 문제 없음)이지만, 세션은 같은 대화가
// 여러 날에 걸쳐 있으면 중복되므로 id 집합을 합집합(union)해야 정확하다.
export function rebucketToolSeries(toolSeries, granularity, range) {
  const keyFn = granularity === "week" ? weekStart : granularity === "month" ? monthStart : (d) => d;
  const by = new Map();
  if (range?.from && range?.to) {
    for (const bucket of generateBucketKeys(range.from, range.to, granularity)) {
      by.set(bucket, { bucket, byTool: {}, byToolEngine: {}, byToolEngineSessions: {} });
    }
  }
  for (const p of toolSeries || []) {
    const key = keyFn(p.bucket);
    if (!by.has(key)) by.set(key, { bucket: key, byTool: {}, byToolEngine: {}, byToolEngineSessions: {} });
    const row = by.get(key);
    for (const [tool, count] of Object.entries(p.byTool || {})) {
      row.byTool[tool] = (row.byTool[tool] || 0) + count;
    }
    for (const [toolEngine, count] of Object.entries(p.byToolEngine || {})) {
      row.byToolEngine[toolEngine] = (row.byToolEngine[toolEngine] || 0) + count;
    }
    for (const [toolEngine, ids] of Object.entries(p.byToolEngineSessionIds || {})) {
      if (!row.byToolEngineSessions[toolEngine]) row.byToolEngineSessions[toolEngine] = new Set();
      ids.forEach((id) => row.byToolEngineSessions[toolEngine].add(id));
    }
  }
  if (by.size === 0) return [];
  return [...by.values()]
    .sort((a, b) => b.bucket.localeCompare(a.bucket))
    .map((row) => ({
      bucket: row.bucket,
      byTool: row.byTool,
      byToolEngine: row.byToolEngine,
      byToolEngineSessionIds: Object.fromEntries(
        Object.entries(row.byToolEngineSessions).map(([k, s]) => [k, [...s]])
      ),
    }));
}

// rebucketToolSeries 결과(최신순)를 "구간별"이 아니라 "누적"(그 시점까지 총 몇 건인지)으로
// 바꾼다. 도구/엔진마다 처음 등장한 구간부터 매 구간 누적합을 이어가며, 특정 구간에 그
// 도구/엔진 사용이 없어도(byToolEngine 에 키가 없어도) 직전 누적치를 그대로 이어받는다
// (없앤 게 아니라 "그 구간엔 추가 사용이 0건이었을 뿐"이라 누적치가 줄면 안 됨).
// 세션 id 는 카운트가 아니라 집합을 이어붙여야(합집합) "그때까지의 순수 누적 세션 수"가 된다.
export function cumulativeToolSeries(series) {
  if (!series || series.length === 0) return [];
  const asc = [...series].sort((a, b) => a.bucket.localeCompare(b.bucket));
  const running = {};
  const runningSessions = {};
  const out = asc.map((r) => {
    for (const [key, count] of Object.entries(r.byToolEngine || {})) {
      running[key] = (running[key] || 0) + count;
    }
    for (const [key, ids] of Object.entries(r.byToolEngineSessionIds || {})) {
      if (!runningSessions[key]) runningSessions[key] = new Set();
      ids.forEach((id) => runningSessions[key].add(id));
    }
    return {
      ...r,
      byToolEngine: { ...running },
      byToolEngineSessionIds: Object.fromEntries(
        Object.entries(runningSessions).map(([k, s]) => [k, [...s]])
      ),
    };
  });
  return out.sort((a, b) => b.bucket.localeCompare(a.bucket));
}

// 백엔드 `?byModelCost=1` 의 modelCostSeries([{bucket, byModel(USD), byModelCount(요청 수)}])를
// 일별→주/월별로 재집계 — "사용량" 탭의 모델별 추이용(2026-07-18). 요청 수는 단순 합산으로
// 충분(유저/세션과 달리 중복 집계 문제 없음). range 를 주면 활동 없는 버킷도 0으로 채운다.
export function rebucketModelCountSeries(series, granularity, range) {
  const keyFn = granularity === "week" ? weekStart : granularity === "month" ? monthStart : (d) => d;
  const by = new Map();
  if (range?.from && range?.to) {
    for (const bucket of generateBucketKeys(range.from, range.to, granularity)) {
      by.set(bucket, { bucket, byModel: {} });
    }
  }
  for (const p of series || []) {
    const key = keyFn(p.bucket);
    if (!by.has(key)) by.set(key, { bucket: key, byModel: {} });
    const row = by.get(key);
    for (const [m, c] of Object.entries(p.byModelCount || {})) {
      row.byModel[m] = (row.byModel[m] || 0) + c;
    }
  }
  if (by.size === 0) return [];
  return [...by.values()].sort((a, b) => b.bucket.localeCompare(a.bucket));
}

// rebucketModelCountSeries 결과(최신순)를 누적으로 — 모델마다 처음 등장한 구간부터 누적합.
export function cumulativeModelCountSeries(series) {
  if (!series || series.length === 0) return [];
  const asc = [...series].sort((a, b) => a.bucket.localeCompare(b.bucket));
  const running = {};
  const out = asc.map((r) => {
    for (const [m, c] of Object.entries(r.byModel || {})) running[m] = (running[m] || 0) + c;
    return { ...r, byModel: { ...running } };
  });
  return out.sort((a, b) => b.bucket.localeCompare(a.bucket));
}

// "요청 수/메시지 수/세션 수" 단위 전환용 — 요청·메시지는 그냥 곱셈이지만 세션은
// byToolEngineSessionIds 의 길이(그 구간·그 도구+엔진의 고유 대화 수)를 써야 한다.
export function toolEngineValue(row, key, unit) {
  if (unit === "sessions") return (row.byToolEngineSessionIds?.[key] || []).length;
  const count = row.byToolEngine?.[key] || 0;
  return unit === "messages" ? count * 2 : count;
}

// 세션 수는 여러 도구/엔진(행 합계)이나 여러 기간(열 합계)에 걸쳐 그냥 더하면 같은 대화가
// 두 도구를 다 썼거나 두 기간에 걸쳐 있을 때 중복 집계된다 — 합집합(union) 크기로 계산.
export function unionSessionCount(rows, keys) {
  const ids = new Set();
  rows.forEach((r) => {
    keys.forEach((k) => {
      (r.byToolEngineSessionIds?.[k] || []).forEach((id) => ids.add(id));
    });
  });
  return ids.size;
}
